import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { HeartPulse, User, Calendar, Shield, Activity, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { SCREENING_SECTIONS } from '../lib/screeningQuestions';

export default function ReportTemplate({ assessmentData }) {
    if (!assessmentData) return null;

    const { 
        type, score, date, questions = [], answers = {}, 
        remarks = {}, patientName, age = 5, patientId = 'REF-1024',
        assessorName = 'Clinical Portal Auto-Generated'
    } = assessmentData;

    const isScreening = type === 'Child Developmental Screening' || type === 'Child Developmental Screening (Final)';
    
    // Calculate metrics
    const allQuestions = isScreening ? SCREENING_SECTIONS.flatMap(s => s.questions) : [];
    const quantitativeQuestions = isScreening ? allQuestions.filter(q => q.options) : questions;
    const maxScore = isScreening ? quantitativeQuestions.length : (questions.length > 0 ? questions.length * 4 : 20);
    
    let finalScore = score;
    if (isScreening) {
        finalScore = quantitativeQuestions.filter(q => answers[q.id] === 'Yes').length;
    } else if (!finalScore) {
        finalScore = Math.round(maxScore * 0.75); 
    }

    const percentage = Math.max(0, Math.min(100, Math.round((finalScore / (maxScore || 1)) * 100)));

    const barData = isScreening ? SCREENING_SECTIONS.map(s => {
        const sectionQuestions = s.questions.filter(q => q.options);
        const sectionYes = sectionQuestions.filter(q => answers[q.id] === 'Yes').length;
        return { name: s.title, score: Math.round((sectionYes / (sectionQuestions.length || 1)) * 100) };
    }) : [
        { name: 'Communication', score: 85 },
        { name: 'ADL', score: 72 },
        { name: 'Social', score: 68 },
        { name: 'Motor', score: percentage },
    ];

    const SCORES = { 
        4: "Always", 3: "Often", 2: "Sometimes", 1: "Rarely",
        'Yes': 'Yes', 'No': 'No', 'Not Sure': 'Not Sure',
        'With Help': 'With Help', 'Partially': 'Partially'
    };

    // For the KPI table in screening, we show the first few questions from each major section
    const displayItems = isScreening 
        ? SCREENING_SECTIONS.flatMap(s => s.questions).filter(q => !q.id.includes('Desc') && q.label).slice(0, 10)
        : questions.slice(0, 8).map((q, i) => ({ label: q, id: i }));

    return (
        <div className="bg-white min-h-screen w-full max-w-[1000px] mx-auto p-16 print:p-0 print:m-0 font-inter text-slate-900 border border-slate-100 shadow-2xl print:shadow-none print:border-0 relative report-print-container">
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    @page { size: A4; margin: 10mm; }
                    
                    /* Aggressive UI Hiding - Hide everything except the report container */
                    body * { visibility: hidden !important; }
                    .report-print-container, .report-print-container * { visibility: visible !important; }
                    .report-print-container { 
                        position: absolute !important; 
                        left: 0 !important; 
                        top: 0 !important; 
                        width: 100% !important; 
                        visibility: visible !important;
                        display: block !important;
                    }

                    body { background: white !important; margin: 0 !important; padding: 0 !important; }
                    .print-break-before { page-break-before: always; }
                    .print-avoid-break { page-break-inside: avoid; display: block !important; }
                    .recharts-surface { max-width: 100% !important; height: auto !important; }
                }
            `}} />

            {/* LOGO & BRANDING HEADER */}
            <div className="mb-12 border-b-4 border-slate-900 pb-8 flex justify-between items-end">
                <div className="flex items-center gap-5">
                    <div className="h-20 w-20 rounded-[1.5rem] bg-brand-600 flex items-center justify-center text-white shadow-lg print:shadow-none">
                        <HeartPulse className="h-10 w-10" />
                    </div>
                    <div>
                        <h1 className="text-4xl font-black tracking-tighter uppercase font-outfit leading-none">Melete-Nova</h1>
                        <p className="text-brand-600 font-black text-xs uppercase tracking-[0.3em] mt-2">Precision Clinical Screening Platform</p>
                    </div>
                </div>
                <div className="text-right space-y-1">
                    <h2 className="text-xl font-black uppercase text-slate-900 font-outfit">Official Clinical Record</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Document ID: RE-{patientId}-{Date.now().toString().slice(-4)}</p>
                </div>
            </div>

            {/* PATIENT & CLINICAL META */}
            <div className="flex flex-col md:flex-row gap-6 mb-12 bg-slate-50 p-6 rounded-[1.5rem] border border-slate-100 print:bg-slate-50/50 print:flex-row print:gap-10">
                <div className="flex-1 space-y-3 border-b md:border-b-0 md:border-r border-slate-200 pb-4 md:pb-0 md:pr-4">
                    <h3 className="text-[9px] font-black text-brand-600 uppercase tracking-widest flex items-center gap-2 mb-2">
                        <User className="h-3 w-3" /> Identity
                    </h3>
                    <p className="text-lg font-black text-slate-900 leading-tight">{patientName}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{age} Yrs • REF: {patientId}</p>
                </div>

                <div className="flex-1 space-y-3 border-b md:border-b-0 md:border-r border-slate-200 pb-4 md:pb-0 md:pr-4">
                    <h3 className="text-[9px] font-black text-brand-600 uppercase tracking-widest flex items-center gap-2 mb-2">
                        <Calendar className="h-3 w-3" /> Context
                    </h3>
                    <p className="text-lg font-black text-slate-900 leading-tight uppercase font-outfit">{type}</p>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{new Date(date).toLocaleDateString(undefined, { dateStyle: 'long' })}</p>
                </div>

                <div className="flex-1 space-y-3">
                    <h3 className="text-[9px] font-black text-brand-600 uppercase tracking-widest flex items-center gap-2 mb-2">
                        <Shield className="h-3 w-3" /> Professional
                    </h3>
                    <p className="text-sm font-bold text-slate-900 leading-tight">{assessorName}</p>
                    <Badge className="bg-brand-600 text-white border-0 px-3 py-1 text-[8px] font-black uppercase tracking-widest rounded-md mt-1">Verified Protocol</Badge>
                </div>
            </div>

            {/* EXECUTIVE SUMMARY */}
            <div className="flex flex-col md:flex-row gap-8 mb-12 print-avoid-break print:flex-row print:gap-10">
                <div className="w-full md:w-1/3 bg-slate-900 text-white rounded-[1.5rem] p-8 flex flex-col items-center justify-center text-center shadow-lg print:shadow-none min-h-[160px]">
                    <Activity className="h-6 w-6 text-brand-400 mb-3" />
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">{isScreening ? 'Milestone Completion' : 'Performance Index'}</p>
                    <h4 className="text-5xl font-black font-outfit text-brand-400">{percentage}%</h4>
                </div>
                <div className="w-full md:w-2/3 bg-white border border-slate-100 rounded-[1.5rem] p-8 flex flex-col justify-center shadow-sm">
                    <h3 className="text-[9px] font-black text-slate-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                         Outcome Narrative
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed font-medium italic">
                        {isScreening 
                            ? `"The developmental screening for ${patientName} indicates a ${percentage}% alignment with age-appropriate milestones. Key benchmarks in core domains show consistent progress, while specific areas may require clinical follow-up for optimization."`
                            : `"The subject exhibits a robust trajectory in ${type}. Current observations suggest ${percentage > 70 ? 'advanced integration' : 'developing proficiency'} within the tested domains."`
                        }
                    </p>
                </div>
            </div>

            {/* DETAILED DATA TABLE */}
            <div className="mb-12 print-avoid-break">
                <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Detailed Response Summary</h3>
                <div className="border border-slate-100 rounded-[1.5rem] overflow-hidden shadow-sm">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-4 text-[9px] font-black text-slate-500 uppercase tracking-widest">Protocol Item</th>
                                <th className="p-4 text-[9px] font-black text-slate-500 uppercase tracking-widest text-center">Result</th>
                                <th className="p-4 text-[9px] font-black text-slate-500 uppercase tracking-widest text-right">Observations</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {displayItems.map((item, i) => {
                                const answer = answers[item.id];
                                const remark = isScreening ? (answers[item.id + 'Desc'] || '') : remarks[item.id];
                                return (
                                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="p-4 text-xs font-bold text-slate-900">{isScreening ? item.label : item.label}</td>
                                        <td className="p-4 text-center">
                                            <span className={`inline-flex px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest ${
                                                (answer === 'Yes' || Number(answer) >= 3) ? 'bg-green-50 text-green-600' : 'bg-brand-50 text-brand-600'
                                            }`}>
                                                {SCORES[answer] || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right text-[10px] text-slate-400 font-medium italic">
                                            {remark || 'Nominal observance.'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* VISUAL ANALYSIS */}
            <div className="flex flex-col md:flex-row gap-8 mb-12 print-avoid-break print:flex-row print:gap-10">
                <div className="flex-1 bg-slate-50 rounded-[1.5rem] p-6 border border-slate-100 min-h-[220px]">
                    <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-6">{isScreening ? 'Domain Proficiency' : 'Maturity Index'}</h3>
                    <div className="h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 20 }}>
                                <XAxis type="number" hide domain={[0, 100]} />
                                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{fontSize: 8, fill: '#94a3b8', fontWeight: 700}} width={120} />
                                <Bar dataKey="score" fill="#0d9488" radius={[0, 6, 6, 0]} barSize={16} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="flex-1 bg-brand-600 text-white rounded-[1.5rem] p-8 flex flex-col justify-center shadow-lg print:shadow-none min-h-[220px]">
                    <h3 className="text-[9px] font-black uppercase tracking-widest mb-4">Clinical Guidance</h3>
                    <div className="flex items-center gap-3 mb-3">
                        <TrendingUp className="h-6 w-6 text-brand-300" />
                        <p className="text-2xl font-black font-outfit uppercase leading-none">Trajectory: {percentage > 50 ? 'Positive' : 'Stable'}</p>
                    </div>
                    <p className="text-xs text-brand-100 leading-relaxed font-medium">
                        {isScreening 
                            ? "Current data indicates the patient is meeting the majority of age-expected developmental benchmarks. Continued monitoring of motor and social integration is recommended."
                            : "Traversing clinical markers with consistent improvement. Longitudinal maturity remains aligned with institutional expectations."
                        }
                    </p>
                </div>
            </div>

            {/* FOOTER ATTESTATION */}
            <div className="pt-12 border-t-2 border-slate-100 flex justify-between items-start opacity-70">
                <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-900">Melete-Nova Clinical Systems</p>
                    <p className="text-[10px] font-medium text-slate-500">© 2026 Melete-Nova Health. All rights reserved.</p>
                </div>
                <div className="text-right space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-900">Certified Digital Record</p>
                    <p className="text-[9px] text-slate-400 italic max-w-sm">This is a digitally generated clinical record. Accuracy verified by provider portal protocols.</p>
                </div>
            </div>
        </div>
    );
}
